<script setup>
import { onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue'
import { computePosition, flip, size } from '@floating-ui/dom'
import { onClickOutside, useEventListener, useResizeObserver, useWindowScroll } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

defineProps({
  options: Array,
})

const isOpen = ref(false)
const menuStyle = shallowRef({})
const elToggle = useTemplateRef('toggle')
const elMenu = useTemplateRef('menu')
const scrollableParent = ref(null)
const selected = defineModel({
  type: Array,
  default: [],
})

const { isScrolling } = useWindowScroll()

useEventListener(scrollableParent, 'scroll', () => {
  isOpen.value = false
})

useResizeObserver(scrollableParent, restyleDropdown, { box: 'device-pixel-content-box' })

onClickOutside(
  elMenu,
  () => {
    isOpen.value = false
  },
  { ignore: [elToggle] }
)

watch(isScrolling, () => {
  restyleDropdown()
})

watch(isOpen, isOpen => {
  if (!isOpen) {
    return
  }

  restyleDropdown()
})

onMounted(() => {
  const sp = getScrollableParent(elToggle.value)

  if (sp) {
    scrollableParent.value = sp
  }
})

async function restyleDropdown() {
  const { x, y } = await computePosition(elToggle.value, elMenu.value, {
    placement: 'bottom-start',
    middleware: [
      flip(),
      size({
        padding: 5,
        apply({ availableWidth, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            overflow: 'auto',
            maxWidth: `${Math.max(0, availableWidth)}px`,
            maxHeight: `${Math.max(0, availableHeight)}px`,
          })
        },
      }),
    ],
  })

  menuStyle.value = {
    left: `${x}px`,
    top: `${y}px`,
  }
}

function getScrollableParent(el) {
  if (el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight) {
    return el
  }

  if (el.parentElement) {
    return getScrollableParent(el.parentElement)
  }

  return null
}

function toggleSelection(item) {
  const sel = [...selected.value]
  const idx = sel.indexOf(item.value)

  if (idx === -1) {
    sel.push(item.value)
  } else {
    sel.splice(idx, 1)
  }

  selected.value = sel
}
function trigclick() {
  isOpen.value = !isOpen.value
}
</script>
<style scoped>
.dropdown-toggle {
  font-size: small;
}
.dropdown-item.active {
  padding-left: calc(var(--bs-dropdown-padding-x) - 1.5rem);
}
.dropdown-menu {
  transition: all 0.3s;
}
</style>
<template>
  <div class="dropdown">
    <i
      class="bi bi-x-circle pointer"
      @click="selected = []"
      role="button"
      v-if="selected.length"
    />

    <button
      type="button"
      class="btn dropdown-toggle btn-sm"
      @click="trigclick"
      ref="toggle"
    >
      {{ selected.length ? t('selected', { count: selected.length }) : t('select') }}
    </button>
    <Teleport to="body">
      <ul
        class="dropdown-menu show"
        v-show="isOpen"
        ref="menu"
        :style="menuStyle"
      >
        <li>
          <div
            type="button"
            class="dropdown-item"
            :class="{ active: selected.includes(item.value) }"
            v-for="item in options"
            :key="item.value"
            @click="toggleSelection(item)"
          >
            <i
              class="bi"
              :class="{ 'bi-check': selected.includes(item.value) }"
            />
            {{ item.label }}
          </div>
        </li>
      </ul>
    </Teleport>
  </div>
</template>

<i18n>
{
  "ru": {
    "selected": "Выбрано {count}",
    "select": "Выберите"
  },
  "en": {
    "selected": "{count} selected",
    "select": "Select"
  }
}
</i18n>
